import { api } from './api';
import type {
  Unit,
  UnitMinimal,
  UnitCreateRequest,
  DeleteResponse,
  PaginatedResponse,
} from '../types';

// API Response wrapper type
interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

export interface UnitQueryParams {
  include_deleted?: boolean;
  districtId?: string;  // Changed from district to districtId
  unitTypeId?: string;
  departmentId?: string;
  parentUnitId?: string;
  page?: number;
  page_size?: number;
  search?: string;  // Search by name, policeReferenceId, or city
  unitCode?: string;  // Search by unit code
  sort_by?: string;   // Column/field to sort by (e.g., 'name', 'email')
  sort_order?: 'asc' | 'desc';  // Sort direction
  filters?: string;   // Per-column filters as JSON
  app_id?: string;    // Filter to one accessible project (intersected server-side with the caller's scope)
  include_global?: boolean;  // Also include core/global rows (appId=null)
}

/** Re-scope preview: which projects reference this unit (child units / villages). */
export interface UnitRescopePreview {
  canRescope: boolean;
  parentConflict?: boolean;
  conflicts: Record<string, { units: number; villages: number }>;
  references: Record<string, { units: number; villages: number }>;
}

export interface UnitMinimalQueryParams {
  search?: string;
  departmentId?: string;
  parentUnitId?: string;
  districtId?: string;
  include_deleted?: boolean;
}

// OrgNode interface for tree structure
export interface OrgNode {
  id: string;
  name: string;
  role: string;
  department: string;
  location: string;
  status?: string;
  imageUrl?: string;
  reportCount?: number;
  isDirector?: boolean;
  children?: OrgNode[];
}

export const unitsService = {
  // Returns full paginated response with metadata
  getAll: async (params?: UnitQueryParams): Promise<PaginatedResponse<Unit>> => {
    const response = await api.get<PaginatedResponse<Unit>>('/api/v1/units/list', { params });
    return response.data;
  },

  // Get all units for dropdown (returns array, no pagination).
  // Pass a large page_size so EVERY unit resolves — otherwise the BE default
  // page size caps the result and unit names fall back to raw ObjectIds.
  getAllForDropdown: async (): Promise<Unit[]> => {
    const response = await api.get<PaginatedResponse<Unit>>('/api/v1/units/list', {
      params: { include_deleted: false, page: 1, page_size: 1000 },
    });
    return response.data.data || [];
  },

  // Get all units for export (returns array, no pagination)
  getAllForExport: async (params?: Omit<UnitQueryParams, 'page' | 'page_size'>): Promise<Unit[]> => {
    const response = await api.get<PaginatedResponse<Unit>>('/api/v1/units/list', {
      params: { ...params, page_size: 1000 },
    });
    return response.data.data || [];
  },

  // Lightweight endpoint for dropdowns/selects
  getMinimalList: async (params?: UnitMinimalQueryParams): Promise<UnitMinimal[]> => {
    const response = await api.get<ApiResponse<UnitMinimal[]>>('/api/v1/units/list-minimal', { params });
    return response.data.data;
  },

  // ALL units (no page-size cap), lightweight, shaped for the hierarchy tree
  // ({ _id, name, parentUnitId, unitCode }). Cached via React Query where used.
  getMinimalTree: async (): Promise<Array<{ _id: string; name: string; parentUnitId?: string | null; unitCode?: string | null; appId?: string | null }>> => {
    const response = await api.get<ApiResponse<UnitMinimal[]>>('/api/v1/units/list-minimal');
    return (response.data.data || []).map((u) => ({
      _id: u.id,
      name: u.name,
      parentUnitId: u.parentUnitId ?? null,
      unitCode: u.unitCode ?? null,
      appId: (u as any).appId ?? null,
    }));
  },

  getById: async (id: string): Promise<Unit> => {
    const response = await api.get<ApiResponse<Unit>>(`/api/v1/units/get/${id}`);
    return response.data.data;
  },

  // Get unit hierarchy (unit and its parent units) - returns single unit with nested parentUnit
  getUnitHierarchy: async (id: string): Promise<Unit> => {
    const response = await api.get<ApiResponse<Unit>>(`/api/v1/units/get/${id}`);
    return response.data.data;
  },

  create: async (data: UnitCreateRequest): Promise<Unit> => {
    const response = await api.post<ApiResponse<Unit>>('/api/v1/units/create', data);
    return response.data.data;
  },

  update: async (id: string, data: Partial<UnitCreateRequest>): Promise<Unit> => {
    const response = await api.put<ApiResponse<Unit>>(`/api/v1/units/update/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<DeleteResponse> => {
    const response = await api.delete<ApiResponse<DeleteResponse>>(`/api/v1/units/delete/${id}`);
    return response.data.data;
  },

  // Preview re-scoping a unit to a project (empty appId = global). Admin action.
  getRescopePreview: async (id: string, appId?: string | null): Promise<UnitRescopePreview> => {
    const response = await api.get<{ data: UnitRescopePreview }>(
      `/api/v1/units/${id}/rescope-preview`,
      { params: { app_id: appId || undefined } }
    );
    return response.data.data;
  },

  // Change a unit's owning project (null/empty = global). Blocks if it would orphan references.
  rescopeProject: async (id: string, appId: string | null): Promise<Unit> => {
    const response = await api.patch<ApiResponse<Unit>>(
      `/api/v1/units/${id}/project`,
      { appId: appId || null }
    );
    return response.data.data;
  },

  restore: async (id: string): Promise<Unit> => {
    const response = await api.patch<ApiResponse<Unit>>(`/api/v1/units/restore/${id}`);
    return response.data.data;
  },

  // Search is handled via the getAll method with search parameter in list endpoint
  search: async (searchTerm: string): Promise<PaginatedResponse<Unit>> => {
    const response = await api.get<PaginatedResponse<Unit>>('/api/v1/units/list', {
      params: { search: searchTerm }
    });
    return response.data;
  },

  // ============================================================
  // UNIT HIERARCHY / TREE STRUCTURE
  // ============================================================

  /**
   * Get all units for hierarchical tree structure
   * Returns flat list of units for building tree in UI
   */
  getUnitTree: async (appId?: string): Promise<Unit[]> => {
    // /list with no page/page_size returns ALL units as a flat array under `data`.
    const response = await api.get<ApiResponse<Unit[]>>('/api/v1/units/list', {
      params: { include_deleted: false, ...(appId ? { app_id: appId } : {}) }
    });
    return response.data.data || [];
  },

  // ============================================================
  // UNIT POST ASSIGNMENT HELPERS (for personnel onboarding)
  // ============================================================

  /**
   * Get all units with their available (vacant) posts
   * Returns units that have at least one post where assignedUser is null
   */
  getUnitsWithAvailablePosts: async (): Promise<Array<{
    unitId: string;
    unitName: string;
    posts: Array<{
      postCode: string;
      postName: string;
      assignedRoles: string[];
      isUnitHead: boolean;
    }>;
  }>> => {
    // Get all units with their posts
    const response = await api.get<PaginatedResponse<Unit>>('/api/v1/units/list', {
      params: { include_deleted: false, page_size: 1000 },
    });
    const units = response.data.data || [];

    // Filter to units with available posts and transform
    return units
      .map(unit => {
        const availablePosts = (unit.posts || [])
          .filter(post => !post.isDelete && !post.assignedUser)
          .map(post => ({
            postCode: post.postCode,
            postName: post.postName,
            assignedRoles: post.assignedRoles?.map(r => r._id) || [],
            isUnitHead: post.isUnitHead || false,
          }));

        return {
          unitId: unit._id,
          unitName: unit.name,
          posts: availablePosts,
        };
      })
      .filter(unit => unit.posts.length > 0); // Only return units with available posts
  },

  /**
   * Get all posts across all units (both vacant and filled) for display
   */
  getAllPostsForSelection: async (): Promise<Array<{
    unitId: string;
    unitName: string;
    postCode: string;
    postName: string;
    assignedUser: string | null;
    assignedRoles: string[];
    isUnitHead: boolean;
    isVacant: boolean;
  }>> => {
    const response = await api.get<PaginatedResponse<Unit>>('/api/v1/units/list', {
      params: { include_deleted: false, page_size: 1000 },
    });
    const units = response.data.data || [];

    const allPosts: Array<{
      unitId: string;
      unitName: string;
      postCode: string;
      postName: string;
      assignedUser: string | null;
      assignedRoles: string[];
      isUnitHead: boolean;
      isVacant: boolean;
    }> = [];

    units.forEach(unit => {
      (unit.posts || [])
        .filter(post => !post.isDelete)
        .forEach(post => {
          allPosts.push({
            unitId: unit._id,
            unitName: unit.name,
            postCode: post.postCode,
            postName: post.postName,
            assignedUser: post.assignedUser?._id || null,
            assignedRoles: post.assignedRoles?.map(r => r._id) || [],
            isUnitHead: post.isUnitHead || false,
            isVacant: !post.assignedUser,
          });
        });
    });

    return allPosts;
  },
};
